import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, batch } from "react-redux";
import classNames from "classnames";

import ModalComponent from "../../../../general-ui/modal/modal";
import TabHeader from "../../../../general-ui/tab-header/tab-header";
import Select from "../../../../general-ui/select/select";
import { SwitchNew } from "../../../../general-ui/switch/switch-new";
import { UploadButton } from './broadcast-card';
import { EBroadcastTypes, Session, SessionTypesEnum, VideoPlayerControlsSettings } from "../../../../../types/working-model";
import { UpdateAllSessionsOverlayChat } from "../../../../../connection/sessions";
import { updateAllEventSessionsOverlaChat, updateWorkingEventSession } from "../../../../../store/actions/admin/create-event";
import {
	toggleDisplaySessionChat,
	updateSession,
	updateWorkingSession,
	setSessionChatDarkMode
} from "../../../../../store/actions/admin/create-event/session";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import usePrevious from "../../../../../utils/use-previous";
import images from '../../../../../images';
import { OptionalComponent } from "../../../../../utils/optional-component";
import { capitalize } from "../../../../../utils/utils";
import Switch from '../../../../general-ui/switch/switch';
import { Tooltip } from '../../../../general-ui/tooltip/tooltip';
import Icon, { COLORS, ICONS } from '../../../../general-ui/icon';
import './broadcast-card.scss';

enum ETabs {
	Visual = 'Visual',
	Thumbnail = 'Thumbnail'
}

interface BroadcastOptionsModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const BroadcastOptionsModal: React.FC<BroadcastOptionsModalProps> = ({ isOpen, onClose }) => {
	const {
		token,
		workingSession,
		workingEvent,
	} = useTypedSelector(state => ({
		token: state.AuthReducer.token,
		workingSession: state.CreateSessionReducer.workingSession,
		workingEvent: state.CreateEventReducer.workingEvent,
	}));
	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(ETabs.Visual);
	const [isOverlay, setIsOverlay] = useState(!!workingSession?.layout?.overlayChat);
	const [applyAllOverlay, setApplyAllOverlay] = useState(false);
	const [preBroadcastImageFile, setPreBroadcastImageFile] = useState<string | undefined>(workingSession?.pre_broadcast_thumbnail || undefined);
	const [postBroadcastImageFile, setPostBroadcastImageFile] = useState<string | undefined>(workingSession?.post_broadcast_thumbnail || undefined);
	const [uploading, setUploading] = useState(false);
	const [shouldUseDifferentThumbnails, setShouldUseDifferentThumbnails] = useState(!!workingSession?.use_different_thumbnails);
	const [selectedVideoPlayerTheme, setSelectedVideoPlayerTheme] = useState<VideoPlayerControlsSettings['color_theme']>(workingSession?.session_chat_dark_mode_enabled ? 'default' : 'light');
	const [tempTheme, setTempTheme] = useState<VideoPlayerControlsSettings['color_theme'] | ''>('');

	const dispatch = useDispatch();

	// on open, make sure to reset isOverlay
	const previous = usePrevious(open);
	useEffect(() => {
		if (!previous && open) {
			setIsOverlay(!!workingSession?.layout?.overlayChat);
		}
	}, [open, previous, workingSession?.layout?.overlayChat]);

	useEffect(() => setPreBroadcastImageFile(workingSession?.pre_broadcast_thumbnail || undefined), [workingSession?.pre_broadcast_thumbnail]);
	useEffect(() => setPostBroadcastImageFile(workingSession?.post_broadcast_thumbnail || undefined), [workingSession?.post_broadcast_thumbnail]);

	const handleClose = () => {
		setOpen(false);
		// handle jumping state
		setTimeout(() => {
			setPreBroadcastImageFile(workingSession?.pre_broadcast_thumbnail || undefined);
			setPostBroadcastImageFile(workingSession?.post_broadcast_thumbnail || undefined);
			setActiveTab(ETabs.Visual);
		}, 500);
		onClose();
	};

	const handleSave = async () => {
		try {
			if (!token || !workingSession || !workingEvent) return;

			const updatedSession: Session = {
				...workingSession,
				layout: {
					...workingSession.layout,
					overlayChat: isOverlay,
				},
				video_player_controls_settings: {
					...workingSession.video_player_controls_settings,
					color_theme: selectedVideoPlayerTheme
				},
				use_different_thumbnails: shouldUseDifferentThumbnails,
				post_broadcast_thumbnail: postBroadcastImageFile,
				pre_broadcast_thumbnail: preBroadcastImageFile
			};

			if ([ETabs.Visual].includes(activeTab)) {
				batch(() => {
					dispatch(updateSession(updatedSession, token));
					dispatch(updateWorkingSession(updatedSession));
				});

				if (applyAllOverlay) {
					// update all sessions in db and in redux working event
					await UpdateAllSessionsOverlayChat(token, workingEvent.uuid, isOverlay);
					dispatch(updateAllEventSessionsOverlaChat(isOverlay));
				} else {
					dispatch(updateWorkingEventSession(updatedSession));
				}

				tempTheme === 'default' ? setDarkMode(true) : setDarkMode(false);
			}

			if (activeTab === ETabs.Thumbnail) {
				const workingEventSession: Session | undefined = workingEvent?.sessions.find(session => session.uuid === workingSession.uuid);

				// update database and redux
				batch(() => {
					dispatch(updateSession(updatedSession, token));
					dispatch(updateWorkingSession(updatedSession));
					workingEventSession && dispatch(updateWorkingEventSession(updatedSession));
				});
			}
		} catch (e: any) {
			console.error(e?.response || e);
		}
		setTimeout(() => setActiveTab(ETabs.Visual), 500);
		onClose();
	};

	const handleSidePanelClick = () => {
		setIsOverlay(false);
		setApplyAllOverlay(false);
	};

	const handleDeleteImage = (type: 'pre' | 'post') => (setUploadedUri: React.Dispatch<React.SetStateAction<string | undefined>>) => {
		if (!workingSession || !token) return;
		if (type === 'pre') setPreBroadcastImageFile(undefined);
		if (type === 'post') setPostBroadcastImageFile(undefined);
		setUploadedUri(undefined);
	};

	useEffect(() => {
		setOpen(isOpen);
	}, [isOpen]);

	const isFireside = workingSession?.session_type === SessionTypesEnum.fireside;
	const isIFrameBroadcast = workingSession?.session_type === SessionTypesEnum.broadcast && workingSession?.broadcast_type === EBroadcastTypes.embed;

	const toggleSessionChat = useCallback((_: string, on: boolean) => {
		dispatch(toggleDisplaySessionChat(on));
	}, [dispatch]);

	const setDarkMode = useCallback((on: boolean) => {
		dispatch(setSessionChatDarkMode(on));
	}, [dispatch]);

	return (
		<ModalComponent
			open={open}
			closeable={false}
			title="Session Display"
			onRequestClose={handleClose}
			trapFocus={false}
			cancellable
			showOverflow
			footer={
				<div>
					<button onClick={handleClose}>Cancel</button>
					<button disabled={uploading} className="primary" onClick={handleSave}>Save</button>
				</div>
			}
		>
			<div className="broadcast-card-modal">
				<div className="broadcast-card-modal-tabs">
					<TabHeader
						tabs={[ETabs.Visual, ETabs.Thumbnail]}
						onClick={setActiveTab}
					/>
				</div>
				<div className="broadcast-card-modal-content">
					{[ETabs.Visual].includes(activeTab) ? (
						<>
							<div className="session-chat-settings">
								<p className="session-chat-settings-label">Enable audience chat within this session</p>
								<Switch
									value={'displaySessionChat'}
									on={workingSession?.session_chat_enabled ?? true}
									onClick={toggleSessionChat}
								/>
							</div>
							<OptionalComponent display={activeTab === ETabs.Visual}>
								<div className="session-layout-type">
									<OptionalComponent display={isFireside || isIFrameBroadcast}>
										<p>Chat must be in side panel for {isFireside ? "Fireside sessions" : "a Broadcast session with an embedded video player"}.</p>
									</OptionalComponent>
									<div className="session-layout-row">
										<div className={classNames("session-layout-option", { active: isOverlay && workingSession?.session_chat_enabled })}>
											<button onClick={() => setIsOverlay(true)} className="clear no-style inherit-dimensions" disabled={isFireside || isIFrameBroadcast || !workingSession?.session_chat_enabled}>
												<img src={images.ChatOverlay} alt="" />
												<label>Overlay</label>
											</button>
										</div>
										<div className={classNames("session-layout-option", { active: !isOverlay && workingSession?.session_chat_enabled })}>
											<button onClick={handleSidePanelClick} className="clear no-style inherit-dimensions" disabled={!workingSession?.session_chat_enabled}>
												<img src={images.ChatSidebar} alt="" />
												<label>Side Panel</label>
											</button>
										</div>
									</div>
								</div>
							</OptionalComponent>
							<div className='player-theme-label-and-tooltip'>
								<p>Video player theme</p>
								<Tooltip tooltip={'Video player theme controls chat background, video player background, and video player controls.'} position='top-center'>
									<Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={16} />
								</Tooltip>
							</div>
							<Select
								placeholder={capitalize(selectedVideoPlayerTheme)}
								options={[
									{
										label: 'Default',
										value: 'default',
									},
									{
										label: 'Light',
										value: 'light',
									}]}
								selected={selectedVideoPlayerTheme}
								onChange={(e) => {
									setSelectedVideoPlayerTheme(e);
									// e === 'default' ? setDarkMode(true) : setDarkMode(false);
									setTempTheme(e);
								}}
								ignorePositioning
							/>
						</>
					) : (
						<div className="broadcast-thumbnail-container">
							<h6>Add image thumbnail</h6>
							<p className="broadcast-thumbnail-paragraph">Image attendee will see before a video starts and after it ends.</p>
							<UploadButton
								initialImage={preBroadcastImageFile}
								onStartUpload={() => {
									setUploading(true);
								}}
								uploadedImage={url => {
									setPreBroadcastImageFile(url);
									setUploading(false);
								}}
								handleDeleteImage={handleDeleteImage('pre')}
							/>

							<div className="use-different-thumbs-container">
								<SwitchNew
									checked={shouldUseDifferentThumbnails}
									content="Use different image after session ends"
									onChange={() => setShouldUseDifferentThumbnails(prev => !prev)}
								/>
							</div>

							{shouldUseDifferentThumbnails ? (
								<UploadButton
									initialImage={postBroadcastImageFile}
									onStartUpload={() => {
										setUploading(true);
									}}
									uploadedImage={url => {
										setPostBroadcastImageFile(url);
										setUploading(false);
									}}
									handleDeleteImage={handleDeleteImage('post')}
								/>
							) : null}
						</div>
					)}
				</div>
			</div>
		</ModalComponent>
	);
};

export default BroadcastOptionsModal;
